import React, { useState } from 'react';

import { DateTime } from 'luxon';

import { Permission } from '@admin/components/helpers/permission';
import { Spinner } from '@admin/components/spinner';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { FEATURE_FLAG } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { useFeatureFlagsQuery } from '@admin/schema';
import { Button } from '@admin/styled/button';
import { ButtonLink } from '@admin/styled/button_link';
import { Card } from '@admin/styled/card';
import { MutedPanel } from '@admin/styled/muted_panel';
import { Page } from '@admin/styled/page';
import { PageHeader } from '@admin/styled/page_header';
import { PageTitle } from '@admin/styled/page_title';
import { Pill } from '@admin/styled/pill';
import { PillGroup } from '@admin/styled/pill_group';
import { Table } from '@admin/styled/table';
import { TableWrap } from '@admin/styled/table_wrap';

import { FeatureFlagType, UpsertModal } from './upsert_modal';

export const FeatureFlagsList: React.FC<{
  resourceName?: string;
  resourceID?: string;
  editAccess?: boolean;
  /** Shown when embedded and there are no flags for this resource type (defaults to a generic line). */
  emptyStateMessage?: string;
}> = ({ resourceName, resourceID, editAccess, emptyStateMessage }) => {
  useTitle('Feature Flags');
  const canViewFeatureFlags = usePermission(FEATURE_FLAG.resource, FEATURE_FLAG.actions.read);
  const canUpdateFeatureFlags = usePermission(FEATURE_FLAG.resource, FEATURE_FLAG.actions.update);
  const showEditControls = editAccess ?? canUpdateFeatureFlags;
  const isStandalonePage = resourceName === undefined;
  const [showUpsertModal, setShowUpsertModal] = useState<boolean>(false);
  const [editingFeatureFlag, setEditingFeatureFlag] = useState<FeatureFlagType | undefined>(undefined);

  const { data, loading, refetch } = useFeatureFlagsQuery({
    client,
    variables: { resourceName: resourceName },
    skip: !canViewFeatureFlags,
  });

  let featureFlags = data?.featureFlags;
  if (resourceName) {
    featureFlags = featureFlags?.filter((featureFlag) => featureFlag.resourceName === resourceName);
  }

  const upsertModal = showUpsertModal && (
    <UpsertModal
      featureFlag={editingFeatureFlag}
      regionOptions={data?.regions ?? []}
      userOptions={data?.users ?? []}
      warehouseOptions={data?.warehouses ?? []}
      closeModal={() => {
        setEditingFeatureFlag(undefined);
        setShowUpsertModal(false);
        refetch();
      }}
    />
  );

  const listCard = (
    <Card>
      {loading && featureFlags === undefined ? (
        <MutedPanel>
          <Spinner />
        </MutedPanel>
      ) : (featureFlags ?? []).length === 0 ? (
        <MutedPanel>
          {isStandalonePage ? 'No feature flags found.' : (emptyStateMessage ?? 'No feature flags are configured yet.')}
          {!isStandalonePage && showEditControls && canViewFeatureFlags && (
            <>
              {' '}
              Add or edit flags on the <ButtonLink to="/feature_flags">Feature Flags</ButtonLink> page.
            </>
          )}
        </MutedPanel>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                {!resourceName && <th>Resource</th>}
                <th>Description</th>
                <th>Whitelisted</th>
                {showEditControls && (
                  <>
                    <th>Created</th>
                    <th>Updated</th>
                    <th />
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {(featureFlags ?? []).map((featureFlag) => (
                <tr key={featureFlag.id}>
                  <td>{featureFlag.name}</td>
                  {!resourceName && <td>{featureFlag.resourceName}</td>}
                  <td>{featureFlag.description}</td>
                  <td>
                    {resourceID ? (
                      featureFlag.whitelistedObjects.find(
                        (whitelistedObject) => whitelistedObject.id === resourceID,
                      ) ? (
                        'Yes'
                      ) : (
                        'No'
                      )
                    ) : (
                      <PillGroup>
                        {featureFlag.whitelistedObjects.map((object) => (
                          <Pill key={object.id} tone="muted">
                            {object.id} - {object.name}
                          </Pill>
                        ))}
                      </PillGroup>
                    )}
                  </td>
                  {showEditControls && (
                    <>
                      <td>{DateTime.fromISO(featureFlag.createdAt).toLocaleString(DateTime.DATETIME_MED)}</td>
                      <td>{DateTime.fromISO(featureFlag.updatedAt).toLocaleString(DateTime.DATETIME_MED)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          onClick={() => {
                            setEditingFeatureFlag(featureFlag);
                            setShowUpsertModal(true);
                          }}
                        >
                          Edit
                        </Button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Card>
  );

  const listContent = (
    <Permission
      resource={FEATURE_FLAG.resource}
      action={FEATURE_FLAG.actions.read}
      denied={
        isStandalonePage ? (
          <Card>
            <MutedPanel>You do not have permission to view feature flags.</MutedPanel>
          </Card>
        ) : null
      }
    >
      {listCard}
    </Permission>
  );

  if (isStandalonePage) {
    return (
      <Page>
        <PageHeader>
          <PageTitle>Feature Flags</PageTitle>
        </PageHeader>
        {listContent}
        {upsertModal}
      </Page>
    );
  }

  return (
    <>
      {listContent}
      {upsertModal}
    </>
  );
};
